import { FC, useState } from 'react';

import dayjs from 'dayjs';
import { Check, MessageSquare, Pencil, X } from 'lucide-react';
import { isNullish } from 'remeda';
import useSWR from 'swr';

import css from './UserComment.module.css';

import { userProfileService } from 'v2/api/userProfileService';
import { Loader } from 'v2/components/loader/Loader';
import { ProfileCard } from 'v2/components/profileCard/ProfileCard';
import { LONG_DASH } from 'v2/constants';

const MAX_LENGTH = 1000;

const formatDate = (date?: string | null) =>
  isNullish(date) || date === '' ? LONG_DASH : dayjs(date).format('YYYY-MM-DD');

export const UserComment: FC<{ userId: string }> = ({ userId }) => {
  const { data, mutate, isLoading } = useSWR(['getUserComment', userId], userProfileService.getUserComment, {
    keepPreviousData: true,
  });
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const text = data?.text ?? '';

  const onEdit = () => {
    setDraft(text);
    setMode('edit');
  };

  const onClose = () => {
    setDraft(text);
    setMode('view');
  };

  const onSave = async () => {
    setIsSaving(true);
    try {
      const saved = await userProfileService.saveUserComment(userId, draft);
      await mutate(saved, { revalidate: false });
      setMode('view');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ProfileCard className={css.userComment} title="Comment" icon={<MessageSquare width={18} height={18} />}>
      {isLoading ? (
        <Loader />
      ) : (
        <div className={css.body}>
          <div className={css.controls}>
            {mode === 'view' ? (
              <button className={css.iconButton} onClick={onEdit} title="Edit">
                <Pencil width={16} height={16} />
              </button>
            ) : (
              <>
                <button className={css.iconButton} onClick={onSave} disabled={isSaving} title="Save">
                  <Check width={16} height={16} />
                </button>
                <button className={css.iconButton} onClick={onClose} disabled={isSaving} title="Close">
                  <X width={16} height={16} />
                </button>
              </>
            )}
          </div>

          {mode === 'view' ? (
            <>
              {text === '' ? <p className={css.emptyText}>No comment</p> : <p className={css.text}>{text}</p>}
              {data != null && (
                <p className={css.audit}>
                  Created {formatDate(data.createdAt)} by {data.createdByName} · Updated {formatDate(data.updatedAt)} by{' '}
                  {data.updatedByName}
                </p>
              )}
            </>
          ) : (
            <>
              <textarea
                className={css.textarea}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={MAX_LENGTH}
                disabled={isSaving}
                autoFocus
                rows={4}
              />
              <p className={css.charCount}>
                {draft.length} / {MAX_LENGTH}
              </p>
            </>
          )}
        </div>
      )}
    </ProfileCard>
  );
};
